import React from 'react';
import styled from 'styled-components';
import SmartParkingAnimation from './SmartParkingAnimation';
import { FaGooglePlay } from 'react-icons/fa';

const HeroContainer = styled.section`
  background-color: #0a1a1f;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 6rem 2rem 4rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at center, rgba(0, 225, 199, 0.1) 0%, transparent 70%);
    top: 0;
    left: 0;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  width: 100%;
  text-align: center;
  z-index: 2;
`;

const Title = styled.h1`
  font-size: 4rem;
  margin-bottom: 1rem;
  background: linear-gradient(to right, #fff, #00e1c7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 5px 15px rgba(0, 225, 199, 0.3);
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.5rem;
  color: #ccc;
  margin-bottom: 2rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const AppButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 4rem;
  flex-wrap: wrap;
`;

const AppButton = styled.a`
  display: flex;
  align-items: center;
  padding: 1rem 2rem;
  background: linear-gradient(145deg, #1a4d57, #0c1f25);
  color: white;
  text-decoration: none;
  border-radius: 50px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
  font-size: 1.2rem;
  font-weight: bold;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.5);
    background: linear-gradient(145deg, #00e1c7, #0c8a7d);
  }
  
  svg {
    font-size: 2rem;
    margin-right: 0.8rem;
    color: #00e1c7;
  }
`;

const BackgroundElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  overflow: hidden;
`;

const ParkingLine = styled.div`
  position: absolute;
  width: 60px;
  height: 10px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  transform: rotate(${props => props.$rotation}deg);
  top: ${props => props.$top}%;
  left: ${props => props.$left}%;
  opacity: ${props => props.$opacity};
`;

const PSign = styled.div`
  position: absolute;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  background-color: rgba(0, 225, 199, 0.2);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: rgba(0, 225, 199, 0.6);
  font-weight: bold;
  font-size: ${props => props.$size * 0.6}px;
  top: ${props => props.$top}%;
  left: ${props => props.$left}%;
  opacity: ${props => props.$opacity};
`;

const Hero = () => {
  // Generate random parking lines and P signs for background
  const generateParkingLines = () => {
    const lines = [];
    for (let i = 0; i < 15; i++) {
      lines.push({
        id: i,
        rotation: Math.random() * 360,
        top: Math.random() * 100,
        left: Math.random() * 100,
        opacity: Math.random() * 0.15 + 0.05
      });
    }
    return lines;
  };
  
  const generatePSigns = () => {
    const signs = [];
    for (let i = 0; i < 10; i++) {
      signs.push({
        id: i,
        size: Math.random() * 20 + 30,
        top: Math.random() * 100,
        left: Math.random() * 100,
        opacity: Math.random() * 0.2 + 0.1
      });
    }
    return signs;
  };
  
  const parkingLines = generateParkingLines();
  const pSigns = generatePSigns();
  
  return (
    <HeroContainer id="home">
      <BackgroundElements>
        {parkingLines.map(line => (
          <ParkingLine 
            key={line.id}
            $rotation={line.rotation}
            $top={line.top}
            $left={line.left}
            $opacity={line.opacity}
          />
        ))}
        {pSigns.map(sign => (
          <PSign 
            key={sign.id}
            $size={sign.size}
            $top={sign.top}
            $left={sign.left}
            $opacity={sign.opacity}
          >
            P
          </PSign>
        ))}
      </BackgroundElements>
      
      <HeroContent>
        <Title>ParkGanga</Title>
        <Subtitle>
          Smart Car Parking Solution powered by CCTV surveillance and real-time availability tracking
        </Subtitle>
        
        <SmartParkingAnimation />
        
        <AppButtons>
          <AppButton href="https://play.google.com/store/apps/details?id=com.empiinnovationhub.smooth" target="_blank">
            <FaGooglePlay /> Get on Google Play
          </AppButton>
        </AppButtons>
      </HeroContent>
    </HeroContainer>
  );
};

export default Hero;